import React from 'react';
import { Link, Redirect } from 'react-router-dom';
import styled from 'styled-components';

import { Button, Message, Loader } from '@getro/rombo';

import BlankLayout from '../../components/layout/Blank';
import pageHoc from '../../hocs/pageHoc';
import BackupCodes from '../Settings/account/MFASettings/BackupCodes';
import MfaSetupSteps from '../Settings/account/MFASettings/MfaSetupSteps';
import { useMfaSetup, MFA_SETUP_STEP } from './useMfaSetup';

const pageTitle = 'Set up two-factor authentication';

const Container = styled.div`
  max-width: 440px;
  margin: 0 auto;
  padding-top: 40px;
`;

const Title = styled.h1`
  margin: 0 0 16px;
  font-size: 28px;
  font-weight: 700;
  line-height: 1.2;
  color: ${({ theme }) => theme.colors.text.dark};
`;

const Description = styled.p`
  margin: 0 0 24px;
  font-size: 18px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.main};
`;

const Subtext = styled.p`
  margin: 0 0 24px;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.text.subtle};
`;

const LoadingWrap = styled.div`
  padding: 24px 0;
  display: flex;
  justify-content: center;
`;

const MfaSetup = () => {
  const { setupToken, step, setupData, backupCodes, expired, loadError, enrollCode, finishEnrollment } = useMfaSetup();

  if (!setupToken) {
    return <Redirect to="/login" />;
  }

  if (expired) {
    return (
      <BlankLayout>
        <Container>
          <Title>Two-factor setup expired</Title>
          <Description>Your setup session has expired.</Description>
          <Subtext>For your security, please log in again to restart two-factor setup.</Subtext>
          <Button as={Link} to="/login" width="100%" justifyContent="center" size="large">
            Back to log in
          </Button>
        </Container>
      </BlankLayout>
    );
  }

  if (step === MFA_SETUP_STEP.BACKUP_CODES) {
    return (
      <BlankLayout>
        <Container>
          <Title>Save your backup codes</Title>
          <BackupCodes codes={backupCodes} onDone={finishEnrollment} fullWidth />
        </Container>
      </BlankLayout>
    );
  }

  return (
    <BlankLayout>
      <Container>
        <Title>Set up two-factor authentication</Title>
        <Description>Your network requires two-factor authentication to complete your account setup.</Description>
        {loadError && <Message type="error" content="Could not start two-factor setup. Please try again." />}
        {!setupData && !loadError && (
          <LoadingWrap>
            <Loader />
          </LoadingWrap>
        )}
        {setupData && <MfaSetupSteps setupData={setupData} onSubmit={enrollCode} dataCyPrefix="mfa-enroll" />}
      </Container>
    </BlankLayout>
  );
};

export default pageHoc({ title: pageTitle })(MfaSetup);
